import React from "react";
import { ArrowRight, CheckCircle2, School, BookOpen, Users2, Building2, Mail } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";
import { SectionHeading } from "@/components/site/SectionHeading";
import { Sunflower, SunflowerMini } from "@/components/site/Sunflower";

const B2B_IMG =
  "https://images.pexels.com/photos/8612964/pexels-photo-8612964.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=940";

const SCHOOLS_EMAIL = "zahabinada@gmail.com";
const SCHOOLS_EMAIL_SUBJECT = "School / Center Partnership Inquiry";
const SCHOOLS_EMAIL_BODY = "Hi Nada,%0D%0A%0D%0AWe'd love to explore working together. Here's a bit about us:%0D%0A%0D%0A• Organisation: %0D%0A• Number of students / age range: %0D%0A• What we're hoping to achieve: %0D%0A• Best contact person & phone: %0D%0A%0D%0AThank you!";

const offerings = [
  {
    icon: BookOpen,
    title: "Curriculum Development",
    body: "Structured, neurodiversity-inclusive curriculum designed for ages 1–16, aligned with the 'regulation before education' philosophy.",
  },
  {
    icon: Users2,
    title: "Staff Training",
    body: "On-site and online training for teachers, TAs, and school leadership on regulation, sensory support, and de-escalation.",
  },
  {
    icon: School,
    title: "Inclusion Workshops",
    body: "Interactive workshops for whole-school awareness, parent communities, and corporate groups building inclusive cultures.",
  },
  {
    icon: Building2,
    title: "Classroom Design Consultation",
    body: "On-site audits and design plans for sensory-smart, neurodiversity-friendly classrooms and therapy rooms.",
  },
];

const outcomes = [
  "Clear frameworks teachers actually use on Monday morning",
  "Measurable reductions in classroom escalations",
  "Stronger parent trust and engagement",
  "Evidence-informed inclusion policy documents",
];

const interestOptions = [
  "Curriculum Development",
  "Staff Training",
  "Inclusion Workshops",
  "Classroom Design",
];

export default function Schools() {

  return (
    <div data-testid="schools-page">
      <section className="section-pad relative overflow-hidden petal-bg">
        <div className="absolute -top-20 -left-10 opacity-30 pointer-events-none hidden md:block">
          <Sunflower size={260} showLeaves className="animate-slowspin" />
        </div>
        <div className="relative grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <span className="inline-flex items-center gap-2 text-xs tracking-[0.22em] uppercase text-brand-leafDeep bg-brand-honey/50 border border-brand-sun/40 rounded-full px-4 py-1.5 mb-6 w-fit">
              <SunflowerMini size={18} />
              For Schools & Centers
            </span>
            <h1 className="font-heading font-normal text-[2.75rem] sm:text-5xl lg:text-[4.25rem] leading-[1.02] tracking-tight">
              Inclusion is not placement —
              <br />
              it is <span className="sunray-underline">preparation.</span>
            </h1>
            <p className="mt-7 text-brand-muted text-base md:text-lg leading-relaxed max-w-xl font-body">
              Nada partners with nurseries, schools, and learning centers
              across the UAE to build curriculum, train staff, and design
              classrooms that actually work for neurodivergent learners —
              without leaving the rest behind.
            </p>
            <a
              data-testid="schools-hero-proposal-btn"
              href="#proposal"
              className="mt-10 inline-flex items-center gap-2 rounded-full bg-brand-sun hover:bg-brand-sunDeep text-brand-ink px-7 py-3.5 text-sm font-medium transition-all shadow-sunny hover:-translate-y-0.5"
            >
              Request a Proposal <ArrowRight size={16} />
            </a>
          </div>
          <div className="lg:col-span-5">
            <Reveal>
              <div className="relative">
                <div className="rounded-[32px] overflow-hidden border-4 border-brand-sun/40 shadow-earthyLg">
                  <img
                    src={B2B_IMG}
                    alt="A diverse group of children engaging in inclusive learning with a supportive educator"
                    className="w-full h-[460px] md:h-[560px] object-cover"
                  />
                </div>
                <span className="absolute -top-3 left-6 bg-brand-sun text-brand-ink text-[11px] uppercase tracking-[0.22em] font-semibold px-4 py-1.5 rounded-full shadow-sunny">
                  Inclusion in practice
                </span>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="section-pad bg-brand-ivory relative overflow-hidden">
        <div className="absolute top-10 right-10 opacity-20 pointer-events-none hidden lg:block">
          <Sunflower size={180} className="animate-slowspin" />
        </div>
        <div className="relative">
        <SectionHeading
          eyebrow="What we offer"
          title="Four practical ways we work with schools."
        />
        <div className="mt-14 grid md:grid-cols-2 gap-6">
          {offerings.map((o, i) => {
            const tones = [
              "bg-brand-sun/25 text-brand-sunDeep",
              "bg-brand-leaf/20 text-brand-leafDeep",
              "bg-brand-terracotta/20 text-brand-rust",
              "bg-brand-sky/40 text-brand-leafDeep",
            ];
            return (
            <Reveal key={o.title} delay={i * 0.08}>
              <div className="bg-white rounded-[28px] p-8 border-2 border-brand-line hover:border-brand-sun/50 h-full transition-colors">
                <div className={`h-12 w-12 rounded-2xl ${tones[i]} grid place-items-center mb-6`}>
                  <o.icon size={22} />
                </div>
                <h3 className="font-heading text-xl mb-3">{o.title}</h3>
                <p className="text-sm text-brand-muted leading-relaxed font-body">{o.body}</p>
              </div>
            </Reveal>
          );})}
        </div>
        </div>
      </section>

      <section className="section-pad">
        <div className="grid lg:grid-cols-2 gap-14">
          <div>
            <SectionHeading
              eyebrow="Benefits"
              title="Why schools partner with Nada."
              intro="We don't drop a PDF and leave. Every engagement includes training, implementation support, and a clear plan the whole team can follow."
            />
          </div>
          <div className="grid gap-4">
            {outcomes.map((o, i) => (
              <Reveal key={o} delay={i * 0.06}>
                <div className="bg-white rounded-2xl border border-brand-line p-5 flex items-start gap-4">
                  <CheckCircle2
                    size={20}
                    className="text-brand-leaf shrink-0 mt-0.5"
                  />
                  <span className="text-sm text-brand-ink">{o}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PROPOSAL CTA */}
      <section id="proposal" className="section-pad bg-brand-leafDeep text-brand-cream relative overflow-hidden">
        <div className="absolute -top-10 -right-10 opacity-20 pointer-events-none">
          <Sunflower size={240} className="animate-slowspin" />
        </div>
        <div className="relative grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <span className="inline-flex items-center gap-2 text-xs tracking-[0.22em] uppercase text-brand-honey bg-white/10 border border-brand-honey/40 rounded-full px-4 py-1.5 mb-6 w-fit">
              <span className="h-1.5 w-1.5 rounded-full bg-brand-sun" />
              Request a proposal
            </span>
            <h2 className="font-heading font-normal text-3xl sm:text-4xl lg:text-5xl leading-[1.05]">
              Tell us about your school.
            </h2>
            <p className="mt-5 text-brand-cream/85 leading-relaxed font-body max-w-lg">
              Share a bit about your context and goals. Nada typically responds
              within 2 business days with a tailored plan and pricing — no
              forms, no automation, just a real reply from her.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <a
                data-testid="schools-email-btn"
                href={`mailto:${SCHOOLS_EMAIL}?subject=${encodeURIComponent(SCHOOLS_EMAIL_SUBJECT)}&body=${SCHOOLS_EMAIL_BODY}`}
                className="rounded-full bg-brand-sun hover:bg-brand-sunDeep text-brand-ink px-7 py-3.5 text-sm font-medium inline-flex items-center gap-2 transition-colors shadow-sunny"
              >
                <Mail size={16} /> Email Nada
              </a>
            </div>
          </div>

          <div className="lg:col-span-6">
            <Reveal>
              <div className="bg-white/8 border border-white/15 rounded-[28px] p-8 md:p-10 backdrop-blur-sm">
                <p className="text-xs uppercase tracking-[0.22em] text-brand-honey font-body mb-4">
                  Helpful to mention
                </p>
                <ul className="space-y-3 font-body">
                  {[
                    "Your organisation name and location",
                    "Number of students and age range",
                    "Specific challenges or goals (regulation, inclusion, curriculum)",
                    "Areas of interest (training, curriculum, design, workshops)",
                    "Best person and phone for follow-up",
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-3 text-sm text-brand-cream/90">
                      <CheckCircle2 size={18} className="text-brand-sun shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-6 pt-6 border-t border-white/10">
                  <p className="text-xs uppercase tracking-[0.22em] text-brand-honey font-body mb-3">
                    Or interested in
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {interestOptions.map((opt) => (
                      <span
                        key={opt}
                        className="rounded-full bg-white/10 border border-white/15 text-brand-cream/90 px-3 py-1.5 text-xs font-body"
                      >
                        {opt}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
